import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import ImagePreviewWithRequest from '@/components/ImagePreviewWithRequest';
import ImageInquiryDialog from '@/components/ImageInquiryDialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipArrow } from '@/components/Tooltip';
import { useToast } from '@/components/ToastProvider';
import useVarietySwitchStore from '@/stores/varietySwitchStore';
import { createListingInquiry } from '@/api/listing';
import { showSuccessToast, showErrorToast } from '@/utils/toastManager';
import useAuthStore from '@/stores/authStore';
import { redirectToLogin } from '@/utils/authUtils';
import rightIcon from '@/assets/chevron_right.svg';
import searchIcon from '@/assets/search.svg';
import markIcon from '@/assets/mark_white.svg';
import notificationIcon from '@/assets/notification.svg';

const CardGallerySection = ({ images, varieties, cardType, currentCardId, isSold, enablePreview = false, listingId }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [hoveredThumbnail, setHoveredThumbnail] = useState(null);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { toast } = useToast();
  
  // Get the variety switch status from the store
  const { status, setStatusSwitching, setStatusDone } = useVarietySwitchStore();

  // Mutation for creating listing inquiry
  const inquiryMutation = useMutation({
    mutationFn: ({ listingId, types }) => createListingInquiry(listingId, types),
    onSuccess: () => {
      showSuccessToast(t('psaInventory.requestDialog.requestSent'));
      setIsRequestDialogOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('common.api_error'));
    }
  });

  // Determine if we're showing varieties or images
  const isVarietyMode = varieties && varieties.length > 0;
  const items = isVarietyMode ? varieties : images;
  
  // Set the initial selected index based on the current card ID
  useEffect(() => {
    if (isVarietyMode && currentCardId && varieties.length > 0) {
      const index = varieties.findIndex(variety => variety.id === currentCardId);
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [isVarietyMode, currentCardId, varieties]);

  // Show toast notification when a variety is switched and page is loaded
  useEffect(() => {
    // If we're in switching state and we've completed navigation
    if (status === 'switching' && isVarietyMode && currentCardId) {
      // Mark as done to prevent showing the toast again
      setStatusDone();
      // Show the toast notification
      // setTimeout(() => {
      //   toast(t('card.rarity_switched_toast'));
      // }, 300); // Small delay to ensure the toast appears after navigation
    }
  }, [status, isVarietyMode, currentCardId, setStatusDone, toast, t]);
  
  const selectedItem = items[selectedIndex];
  
  const handlePrevItem = () => {
    if (isVarietyMode) {
      // If it's the first time switching varieties, mark as switching
      if (!status) {
        setStatusSwitching();
      }
      
      const newIndex = selectedIndex > 0 ? selectedIndex - 1 : items.length - 1;
      // Navigate to the new variety's card detail page
      navigate(`/card/${items[newIndex].id}`);
      return;
    }
    
    // Regular image mode - just update the index
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
  };

  const handleNextItem = () => {
    if (isVarietyMode) {
      // If it's the first time switching varieties, mark as switching
      if (!status) {
        setStatusSwitching();
      }
      
      const newIndex = selectedIndex < items.length - 1 ? selectedIndex + 1 : 0;
      // Navigate to the new variety's card detail page
      navigate(`/card/${items[newIndex].id}`);
      return;
    }
    
    // Regular image mode - just update the index
    setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
  };
  
  const handleItemClick = (index) => {
    if (isVarietyMode && index !== selectedIndex) {
      // If it's the first time switching varieties, mark as switching
      if (!status) {
        setStatusSwitching();
      }
      
      // Navigate to the new card detail page if it's a different variety
      navigate(`/card/${items[index].id}`);
      return;
    }
    
    // Otherwise just select the index
    setSelectedIndex(index);
  };

  // Determine if we should show the tooltip (only if it's the first time and we're in variety mode and varieties exist)
  const shouldShowTooltip = isVarietyMode && varieties && varieties.length > 1;
  const shouldShowInitTooltip = isVarietyMode && varieties && varieties.length > 1 && !status;

  // Handle image request
  const handleRequestImages = () => {
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    setIsRequestDialogOpen(true);
  };

  const handleRequestSubmit = (types) => {
    if (listingId) {
      inquiryMutation.mutate({
        listingId,
        types
      });
    }
  };

  // Add safety check to prevent errors when data is not ready
  if (!items || items.length === 0 || !selectedItem) {
    return null;
  }

  return (
    <div className="w-full flex gap-6">
      {/* Thumbnails */}
      <div className="w-[83px] h-[620px] flex flex-col gap-6 overflow-y-auto">
        {items.map((item, index) => (
          shouldShowTooltip && index !== selectedIndex ? (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleItemClick(index)}
                  onMouseEnter={() => setHoveredThumbnail(index)}
                  onMouseLeave={() => setHoveredThumbnail(null)}
                  className={`w-[83px] h-[83px] rounded relative ${
                    selectedIndex === index ? 'border-2 border-[#ff5501] bg-[#eeeeee]' : 'bg-white border border-[#DDDDDD]'
                  }`}
                >
                  <img 
                    src={item.imageUrl} 
                    alt={`${item.name} - ${item.rarity}`} 
                    className="w-full h-full object-contain rounded"
                  />
                  {selectedIndex === index && (
                    <div className="absolute top-0 left-0 w-5 h-5 flex items-center justify-center bg-[#ff5501] rounded-br-md">
                      <img src={markIcon} className="w-4 h-4" alt="Selected" />
                    </div>
                  )}
                </button>
              </TooltipTrigger>
              {hoveredThumbnail === index && (
                <TooltipContent side="right" sideOffset={8} align="center">
                  {t('card.switch_rarity_thumbnail_tooltip')}
                  <TooltipArrow className="fill-black/90" offset={12} width={8} height={8} style={{ left: '-8px' }} />
                </TooltipContent>
              )}
            </Tooltip>
          ) : (
            <button
              key={index}
              onClick={() => handleItemClick(index)}
              onMouseEnter={() => isVarietyMode && setHoveredThumbnail(index)}
              onMouseLeave={() => isVarietyMode && setHoveredThumbnail(null)}
              className={`w-[83px] h-[83px] rounded-md relative ${
                selectedIndex === index ? 'border-2 border-[#ff5501] bg-[#eeeeee]' : 'bg-white border border-[#DDDDDD]'
              }`}
            >
              <img 
                src={isVarietyMode ? item.imageUrl : item} 
                alt={isVarietyMode ? `${item.name} - ${item.rarity}` : `Thumbnail ${index + 1}`} 
                className="w-full h-full object-contain rounded"
              />
              {selectedIndex === index && (
                <div className="absolute top-0 left-0 w-5 h-5 flex items-center justify-center bg-[#ff5501] rounded-br-md">
                  <img src={markIcon} className="w-4 h-4" alt="Selected" />
                </div>
              )}
            </button>
          )
        ))}
      </div>

      {/* Main Image */}
      <div className="flex-1 rounded-md h-[620px] relative bg-[#FFFFFF]">
        <img 
          src={isVarietyMode ? selectedItem.imageUrl : selectedItem} 
          alt={isVarietyMode ? `${selectedItem.name} - ${selectedItem.rarity}` : "Card"} 
          className="w-full h-full object-contain rounded-md"
        />
        
        {isVarietyMode && selectedItem.rarity && (
          <div className="absolute bottom-4 left-4 px-3 py-1 bg-white/80 backdrop-blur-sm rounded-md text-sm">
            {selectedItem.rarity}
          </div>
        )}
        
        {isSold && (
          <div className="w-[110px] h-[110px] left-0 top-0 absolute">
            <div className="absolute inset-0">
              <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M110 0H0V110L110 0Z" fill="#FF5601"/>
              </svg>
            </div>
            <div className="left-0 top-[49.50px] absolute origin-top-left -rotate-45 text-white text-2xl font-bold">
              SOLD
            </div>
          </div>
        )}
        
        {/* Navigation Buttons */}
        <div className="absolute right-[16px] top-1/2 -translate-y-1/2">
          {shouldShowInitTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-10 h-[97px] rounded-[48px] border border-[#eeeeee] flex-col justify-center bg-white/90 backdrop-blur-blur">
                  <button 
                    onClick={handlePrevItem}
                    className="w-10 h-[48px] flex items-center justify-center"
                  >
                    <img src={rightIcon} className="w-6 h-6 rotate-[270deg]" alt="Previous" />
                  </button>
                  <div className="w-full h-px bg-[#eeeeee]" />
                  <button 
                    onClick={handleNextItem}
                    className="w-10 h-[48px] flex items-center justify-center"
                  >
                    <img src={rightIcon} className="w-6 h-6 rotate-90" alt="Next" />
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={4}>
                {t('card.switch_rarity_tooltip')}
                <TooltipArrow className="fill-black/90" />
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="w-10 h-[97px] rounded-[48px] border border-[#eeeeee] flex-col justify-center bg-white/90 backdrop-blur-blur">
              <button 
                onClick={handlePrevItem}
                className="w-10 h-[48px] flex items-center justify-center"
              >
                <img src={rightIcon} className="w-6 h-6 rotate-[270deg]" alt="Previous" />
              </button>
              <div className="w-full h-px bg-[#eeeeee]" />
              <button 
                onClick={handleNextItem}
                className="w-10 h-[48px] flex items-center justify-center"
              >
                <img src={rightIcon} className="w-6 h-6 rotate-90" alt="Next" />
              </button>
            </div>
          )}
        </div>

        {/* Request Button - Only show for non-PSA listings when listingId exists */}
        {listingId && cardType !== 'PSA_CARD' && (
          <div className={`absolute right-4 ${enablePreview ? 'bottom-[72px]' : 'bottom-4'}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={() => setIsRequestDialogOpen(true)}
                  className="w-10 h-10 flex items-center justify-center bg-white rounded-full"
                >
                  <img src={notificationIcon} className="w-4 h-4" alt="Request" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={4}>
                {t('card.report_image_issue_tooltip')}
                <TooltipArrow className="fill-black/90" />
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Preview Button - Only show when enablePreview is true */}
        {enablePreview && (
          <div className="absolute right-4 bottom-4">
            <button 
              onClick={() => setPreviewDialogOpen(true)}
              className="w-10 h-10 flex items-center justify-center bg-white rounded-full"
            >
              <img src={searchIcon} className="w-5 h-5" alt="Preview" />
            </button>
          </div>
        )}
      </div>

      {previewDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/85">
          <div className="w-full h-full">
            <ImagePreviewWithRequest
              images={isVarietyMode ? items.map(item => item.imageUrl) : images}
              initialIndex={selectedIndex}
              onClose={() => setPreviewDialogOpen(false)}
              showRequestFeature={true}
              onRequestImages={handleRequestImages}
              className="w-full h-full"
            />
          </div>
        </div>
      )}

      {/* Request Type Dialog */}
      <ImageInquiryDialog
        isOpen={isRequestDialogOpen}
        onClose={() => setIsRequestDialogOpen(false)}
        onSubmit={handleRequestSubmit}
        loading={inquiryMutation.isPending}
        cardType={cardType === 'PSA_CARD' ? 'PSA_SINGLE_CARD' : 'NON_PSA_SINGLE_CARD'}
      />
    </div>
  );
};

export default CardGallerySection; 